import { useState, useMemo } from 'react'
import { getPageUnlockStatus } from '../../shared/utils/pageUnlockStatus'
import { getSubCategory } from '../../shared/data/industries'

// ── Section Header ──────────────────────────────────────────────────────────
function SidebarSection({ label, first }) {
  return (
    <div className={first ? 'pt-2' : 'pt-5'}>
      <p className="px-3 pb-1.5 text-xs font-semibold uppercase tracking-widest" style={{ color: '#5A7080' }}>
        {label}
      </p>
    </div>
  )
}

// ── Nav Item ────────────────────────────────────────────────────────────────
function SidebarNavItem({ label, icon, locked, comingSoon, isActive, onClick, tooltip, isChild }) {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={onClick}
        onMouseEnter={() => locked && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`w-full flex items-center justify-between rounded-lg transition-all duration-150 ${isChild ? 'pr-3 py-1' : 'px-3 py-1.5'}`}
        style={{
          paddingLeft: isChild ? 38 : undefined,
          opacity: (locked || comingSoon) ? 0.4 : 1,
          backgroundColor: isActive ? '#213C4E' : 'transparent',
          color: isActive ? '#ffffff' : isChild ? '#8A9BAA' : '#BCC5CD',
        }}
        onMouseOver={e => {
          if (!isActive && !locked && !comingSoon) { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' }
        }}
        onMouseOut={e => {
          if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = isChild ? '#8A9BAA' : '#BCC5CD' }
        }}
      >
        <span className="flex items-center gap-2.5">
          {icon && !isChild && (
            <span style={{ color: isActive ? '#8AD0D6' : '#7E8F9C', flexShrink: 0 }}>
              {icon}
            </span>
          )}
          <span style={{ fontSize: isChild ? 13 : 13.5, fontWeight: isActive ? 500 : 400 }}>{label}</span>
        </span>

        {/* Right side indicators */}
        {locked && !comingSoon && (
          <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{ opacity: 0.5, color: '#7E8F9C' }}>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
        )}
        {comingSoon && (
          <span style={{ fontSize: 10, fontWeight: 500, padding: '1px 6px', borderRadius: 3, backgroundColor: '#213C4E', color: 'rgba(255,255,255,0.35)' }}>
            Soon
          </span>
        )}
        {isActive && !locked && !comingSoon && (
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#8AD0D6' }} />
        )}
      </button>

      {/* Lock tooltip */}
      {showTooltip && tooltip && (
        <div
          className="absolute left-full top-1/2 -translate-y-1/2 ml-3 z-50 px-3 py-2 rounded-md text-xs max-w-[200px] whitespace-normal pointer-events-none"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)', color: 'rgba(255,255,255,0.85)' }}
        >
          {tooltip}
        </div>
      )}
    </div>
  )
}

// ── Nav Group (collapsible parent) ──────────────────────────────────────────
function SidebarNavGroup({ label, icon, isActive, children, onNavigate }) {
  const [expanded, setExpanded] = useState(true)

  return (
    <div>
      <button
        onClick={() => onNavigate ? onNavigate() : setExpanded(!expanded)}
        className="w-full flex items-center justify-between rounded-lg transition-all duration-150 px-3"
        style={{
          paddingTop: 6, paddingBottom: 6,
          color: isActive ? '#ffffff' : '#BCC5CD',
        }}
        onMouseOver={e => { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#ffffff' }}
        onMouseOut={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = isActive ? '#ffffff' : '#BCC5CD' }}
      >
        <span className="flex items-center gap-2.5">
          {icon && (
            <span style={{ color: isActive ? '#8AD0D6' : '#7E8F9C', flexShrink: 0 }}>
              {icon}
            </span>
          )}
          <span style={{ fontSize: 13.5, fontWeight: 500 }}>{label}</span>
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
          className="p-1 -mr-1 rounded"
          style={{ cursor: 'pointer' }}
          onMouseOver={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)' }}
          onMouseOut={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
        >
          <svg
            className="w-3 h-3 transition-transform duration-200 ease-in-out"
            style={{ color: '#7E8F9C', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {/* Children */}
      <div
        className="overflow-hidden transition-all duration-200 ease-in-out"
        style={{
          maxHeight: expanded ? '500px' : '0px',
          opacity: expanded ? 1 : 0,
        }}
      >
        {children}
      </div>
    </div>
  )
}

// ── Main ClientSidebar ──────────────────────────────────────────────────────
function ClientSidebar({ client, assessment, activeSection, onNavigate }) {
  const unlockStatus = useMemo(() =>
    getPageUnlockStatus(assessment, client),
    [assessment, client]
  )

  const nav = (key) => () => onNavigate(key)
  const active = (key) => activeSection === key
  const groupActive = (keys) => keys.some(k => activeSection === k)

  const icon = (d, size = 16) => (
    <svg style={{ width: size, height: size }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={d} />
    </svg>
  )

  return (
    <div className="flex flex-col h-full">
      {/* Client identity */}
      <div className="px-3 pt-4 pb-3">
        <p className="text-sm font-semibold text-white truncate">{client?.name || 'Client'}</p>
        {client?.industry && (
          <p className="text-[10px] text-gray-400 truncate mt-0.5">
            {(client.industrySubCategory || client.industry_sub_category)
              ? `${client.industry}: ${getSubCategory(client.industry, client.industrySubCategory || client.industry_sub_category)?.label || client.industrySubCategory || client.industry_sub_category}`
              : client.industry
            }
          </p>
        )}
      </div>


      {/* Scrollable nav */}
      <div className="flex-1 overflow-y-auto scrollbar-hide pb-4">
        {/* Overview — standalone */}
        <div className="pt-2 pb-4">
          <SidebarNavItem
            label="Client Overview"
            icon={icon('M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6')}
            isActive={active('overview')}
            onClick={nav('overview')}
          />
        </div>

        {/* DISCOVER */}
        <SidebarSection label="Discover" first />
        <SidebarNavItem
          label="Client Intelligence"
          icon={icon('M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z')}
          isActive={active('client-intelligence')}
          onClick={nav('client-intelligence')}
        />
        <SidebarNavGroup
          label="Assessment"
          icon={icon('M12 22a10 10 0 100-20 10 10 0 000 20zM16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z')}
          isActive={groupActive(['maturity-assessment', 'risk-impact-calibration', 'cyber-insurance-review'])}
        >
          <SidebarNavItem label="Discovery Assessments" isChild isActive={active('maturity-assessment')} onClick={nav('maturity-assessment')} />
          <SidebarNavItem label="Impact Calibration" isChild isActive={active('risk-impact-calibration')} onClick={nav('risk-impact-calibration')} />
          <SidebarNavItem label="Cyber Insurance Review" isChild isActive={active('cyber-insurance-review')} onClick={nav('cyber-insurance-review')} />
        </SidebarNavGroup>

        <SidebarNavGroup
          label="Technology"
          icon={icon('M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01')}
          isActive={groupActive(['tech-assets', 'tech-stack', 'control-testing'])}
        >
          <SidebarNavItem label="Assets" isChild isActive={active('tech-assets')} onClick={nav('tech-assets')} />
          <SidebarNavItem label="Security Tooling" isChild isActive={active('tech-stack')} onClick={nav('tech-stack')} />
          <SidebarNavItem label="Control Testing" isChild comingSoon isActive={active('control-testing')} onClick={nav('control-testing')} />
        </SidebarNavGroup>

        {/* INSIGHTS */}
        <SidebarSection label="Insights" />
        <SidebarNavGroup
          label="Program Overview"
          icon={icon('M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z')}
          isActive={groupActive(['program-overview', 'risk-threats', 'financial-resilience'])}
        >
          <SidebarNavItem label="Program Overview" isChild locked={!unlockStatus['program-overview']?.unlocked} tooltip={unlockStatus['program-overview']?.reason} isActive={active('program-overview')} onClick={nav('program-overview')} />
          <SidebarNavItem label="Risk & Threats" isChild locked={!unlockStatus['risk-threats']?.unlocked} tooltip={unlockStatus['risk-threats']?.reason} isActive={active('risk-threats')} onClick={nav('risk-threats')} />
          <SidebarNavItem label="Financial Resilience" isChild locked={!unlockStatus['financial-resilience']?.unlocked} tooltip={unlockStatus['financial-resilience']?.reason} isActive={active('financial-resilience')} onClick={nav('financial-resilience')} />
        </SidebarNavGroup>

        <SidebarNavGroup
          label="Regulatory Landscape"
          icon={icon('M12 3v18m0-18l-6 2l-3 9a5.002 5.002 0 006.001 0L6 5M12 3l6 2l3 9a5.002 5.002 0 01-6.001 0L18 5M8 21h8')}
          isActive={groupActive(['reg-updates', 'response-guidance', 'compliance-insights'])}
        >
          <SidebarNavItem label="Regulatory Profile" isChild locked={!unlockStatus['compliance-insights']?.unlocked} tooltip={unlockStatus['compliance-insights']?.reason} isActive={active('compliance-insights')} onClick={nav('compliance-insights')} />
          <SidebarNavItem label="Response Guidance" isChild isActive={active('response-guidance')} onClick={nav('response-guidance')} />
          <SidebarNavItem label="Regulatory Updates" isChild isActive={active('reg-updates')} onClick={nav('reg-updates')} />
        </SidebarNavGroup>

        {/* GOVERN — five-bucket program-management hub:
            Frameworks → Enablement Plan → Risk Management → Documentation
            → Advisory Reports. Risk Management groups all risk-surfacing
            items (vendor, audit, pen test) since they all roll up into the
            Risk Register. Documentation groups Policies + Documents. */}
        <SidebarSection label="Govern" />
        {/* Frameworks — pick NIST CSF 2.0, CIS CSC, HIPAA, PCI DSS, ISO 27001,
            SOC 2, NIST 800-53/171, CMMC. Crosswalk to assessment controls. */}
        <SidebarNavItem
          label="Frameworks"
          icon={icon('M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6')}
          comingSoon
          isActive={active('frameworks')}
          onClick={nav('frameworks')}
        />
        <SidebarNavItem
          label="Enablement Plan"
          icon={icon('M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01')}
          locked={!unlockStatus['enablement-plan']?.unlocked}
          tooltip={unlockStatus['enablement-plan']?.reason}
          isActive={active('enablement-plan')}
          onClick={nav('enablement-plan')}
        />
        <SidebarNavGroup
          label="Risk Management"
          icon={icon('M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z')}
          isActive={groupActive(['risk-register', 'third-party-risk', 'audit-findings'])}
        >
          <SidebarNavItem label="Risk Register" isChild comingSoon isActive={active('risk-register')} onClick={nav('risk-register')} />
          <SidebarNavItem label="Third-Party Risk" isChild comingSoon isActive={active('third-party-risk')} onClick={nav('third-party-risk')} />
          <SidebarNavItem label="Audit Findings" isChild comingSoon isActive={active('audit-findings')} onClick={nav('audit-findings')} />
        </SidebarNavGroup>
        <SidebarNavGroup
          label="Documentation"
          icon={icon('M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
          isActive={groupActive(['policies', 'documents'])}
        >
          <SidebarNavItem label="Policies" isChild isActive={active('policies')} onClick={nav('policies')} />
          <SidebarNavItem label="Document Vault" isChild isActive={active('documents')} onClick={nav('documents')} />
        </SidebarNavGroup>
        {/* Advisory Reports — consolidated hub for client-facing exports
            (Executive Advisory, Insurance Review, Risk Appetite Statement,
            Enablement Plan / POA&M, etc.). Replaces the standalone
            Executive Report entry. */}
        <SidebarNavItem
          label="Advisory Reports"
          icon={icon('M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
          comingSoon
          isActive={active('advisory-reports')}
          onClick={nav('advisory-reports')}
        />

        {/* MANAGE */}
        <SidebarSection label="Manage" />
        <SidebarNavItem
          label="Settings"
          icon={(() => (
            <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
            </svg>
          ))()}
          isActive={groupActive(['admin-portal', 'admin-users', 'admin-activity'])}
          onClick={nav('admin-portal')}
        />
        <SidebarNavItem
          label="Integrations"
          icon={(() => (
            <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v3M15 3v3M9 6h6M7 6h10a1 1 0 011 1v4a6 6 0 01-12 0V7a1 1 0 011-1zM12 17v4" />
            </svg>
          ))()}
          comingSoon
          isActive={active('integrations')}
          onClick={nav('integrations')}
        />
      </div>
    </div>
  )
}

export default ClientSidebar
